import React, { useEffect, useMemo, useState } from "react";
import FormControl from "../common/FormControl";
import Button from "../common/Button";

import {
  Megaphone,
  X,
  PlusCircle,
  History,
  Archive,
  Send,
  MessageSquare,
  AlertCircle,
  ShieldAlert,
  CheckCircle,
  Paperclip,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  fetchIncidentTypesDropdown,
  fetchRisksDropdown,
  fetchProcessesDropdown,
  fetchCategoriesDropdown,
  incidentsApi,
  Incident,
} from "../../services/incidentService";

function StatusBadge({ status }: { status: string }) {
  let cls = "neutral";
  let icon: React.ReactNode = null;
  if (status === "Open" || status === "New") {
    cls = "open";
    icon = <AlertCircle size={10} />;
  } else if (
    status.toLowerCase().includes("escalat") ||
    status === "CAPA Assigned"
  ) {
    cls = "escalated";
    icon = <ShieldAlert size={10} />;
  } else if (status.toLowerCase().includes("closed") || status === "Archived") {
    cls = "closed";
    icon = <CheckCircle size={10} />;
  } else if (status === "In Progress") {
    cls = "progress";
  }
  return (
    <span className={`report-incident-status-badge ${cls}`}>
      {icon} {status}
    </span>
  );
}

const OCCURRENCE_OPTIONS = [
  { value: "Actual Event", label: "Actual Event" },
  { value: "Near Miss", label: "Near Miss" },
  { value: "Reported Observation", label: "Reported Observation" },
];

const WhistleBlowerBubble: React.FC = () => {
  const { state } = useAuth();
  const user = state.user as any;

  const [showReporter, setShowReporter] = useState(false);
  const [tab, setTab] = useState<"new" | "history" | "archived">("new");

  const [types, setTypes] = useState<{ id: number; name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [risks, setRisks] = useState<{ id: number; name: string }[]>([]);
  const [processes, setProcesses] = useState<{ id: number; name: string }[]>([]);

  const [form, setForm] = useState({
    incident_master_id: "" as number | "",
    incident_category_id: "" as number | "",
    risk_id: "" as number | "",
    process_id: "" as number | "",
    occurrence_type: "Actual Event",
    description: "",
    isAnonymous: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<{
    kind: "ok" | "err";
    text: string;
  } | null>(null);

  const [activeIncidents, setActiveIncidents] = useState<Incident[]>([]);
  const [archivedIncidents, setArchivedIncidents] = useState<Incident[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!showReporter) return;
    Promise.all([
      fetchIncidentTypesDropdown().then((d) => d.items).catch(() => []),
      fetchCategoriesDropdown().then((d) => d.items).catch(() => []),
      fetchRisksDropdown().then((d) => d.items).catch(() => []),
      fetchProcessesDropdown().then((d) => d.items).catch(() => []),
    ]).then(([t, c, r, p]) => {
      setTypes(t);
      setCategories(c);
      setRisks(r);
      setProcesses(p);
    });
  }, [showReporter]);

  useEffect(() => {
    if (!showReporter) return;
    if (tab === "new") return;
    if (!user) return;
    setHistoryLoading(true);
    const archived = tab === "archived";
    incidentsApi
      .list({ archived })
      .then((items) => {
        const mine = items.filter((i) => i.reported_by_user_id === user.id);
        if (archived) setArchivedIncidents(mine);
        else setActiveIncidents(mine);
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [tab, showReporter, user]);

  const typeOptions = useMemo(
    () => types.map((t) => ({ value: t.id, label: t.name })),
    [types]
  );
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.name })),
    [categories]
  );
  const riskOptions = useMemo(
    () => risks.map((r) => ({ value: r.id, label: r.name })),
    [risks]
  );
  const processOptions = useMemo(
    () => processes.map((p) => ({ value: p.id, label: p.name })),
    [processes]
  );

  const handleSubmit = async () => {
    setSubmitMsg(null);
    if (!form.description.trim()) {
      setSubmitMsg({ kind: "err", text: "Description is required" });
      return;
    }
    const entity_id = user?.entity_id;
    if (!entity_id) {
      setSubmitMsg({
        kind: "err",
        text: "Entity context missing - please log in.",
      });
      return;
    }
    setSubmitting(true);
    const wasAnonymous = form.isAnonymous;
    try {
      const payload = {
        entity_id,
        incident_description: form.description,
        occurrence_type: form.occurrence_type,
        incident_master_id: form.incident_master_id
          ? Number(form.incident_master_id)
          : undefined,
        incident_category_id: form.incident_category_id
          ? Number(form.incident_category_id)
          : undefined,
        risk_id: form.risk_id ? Number(form.risk_id) : undefined,
        process_id: form.process_id ? Number(form.process_id) : undefined,
        is_anonymous: form.isAnonymous,
        reported_by_user_id: form.isAnonymous ? undefined : user?.id,
      };
      const result = await incidentsApi.whistleblower(payload);
      setSubmitMsg({
        kind: "ok",
        text: wasAnonymous
          ? "Submitted anonymously. Thank you."
          : `Submitted as ${result.incident_code}. Thank you.`,
      });
      setForm({
        incident_master_id: "",
        incident_category_id: "",
        risk_id: "",
        process_id: "",
        occurrence_type: "Actual Event",
        description: "",
        isAnonymous: false,
      });
    } catch (e: any) {
      setSubmitMsg({
        kind: "err",
        text: e?.response?.data?.detail || e?.message || "Submit failed",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="BTN_wb_fab_open"
        onClick={() => setShowReporter(true)}
        className="report-incident-fab"
        aria-label="Report Incident"
      >
        <span className="report-incident-fab-icon">
          <Megaphone size={22} />
        </span>
        <span className="report-incident-fab-label">Report Incident</span>
      </button>

      {showReporter && (
        <div className="report-incident-popup" role="dialog" aria-modal="false">
          <div className="report-incident-popup-header">
            <div className="report-incident-popup-header-title">
              <Megaphone size={18} />
              <h3>Incident Reporter</h3>
            </div>
            <button
              type="button"
              data-testid="BTN_wb_close"
              onClick={() => setShowReporter(false)}
              className="report-incident-popup-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="report-incident-popup-tabs">
            <button
              type="button"
              data-testid="BTN_wb_tab_new"
              onClick={() => setTab("new")}
              className={`report-incident-popup-tab ${tab === "new" ? "active" : ""}`}
            >
              <PlusCircle size={13} /> New
            </button>
            <button
              type="button"
              data-testid="BTN_wb_tab_history"
              onClick={() => setTab("history")}
              className={`report-incident-popup-tab ${tab === "history" ? "active" : ""}`}
            >
              <History size={13} /> Active
            </button>
            <button
              type="button"
              data-testid="BTN_wb_tab_archived"
              onClick={() => setTab("archived")}
              className={`report-incident-popup-tab ${tab === "archived" ? "active" : ""}`}
            >
              <Archive size={13} /> Archived
            </button>
          </div>

          <div className="report-incident-popup-body">
            {tab === "new" && (
              <>
                <FormControl.SearchSelect
                  data-testid="SEL_wb_incident_type"
                  label="Incident Type"
                  options={typeOptions}
                  value={form.incident_master_id}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      incident_master_id: v ? Number(v) : "",
                    })
                  }
                  placeholder="Select type…"
                  clearable
                />
                <FormControl.SearchSelect
                  data-testid="SEL_wb_category"
                  label="Category"
                  options={categoryOptions}
                  value={form.incident_category_id}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      incident_category_id: v ? Number(v) : "",
                    })
                  }
                  placeholder="Select category…"
                  clearable
                />
                <div className="report-incident-popup-row-2col">
                  <FormControl.SearchSelect
                    data-testid="SEL_wb_risk"
                    label="Risk"
                    options={riskOptions}
                    value={form.risk_id}
                    onChange={(v) =>
                      setForm({ ...form, risk_id: v ? Number(v) : "" })
                    }
                    placeholder="Optional…"
                    clearable
                  />
                  <FormControl.SearchSelect
                    data-testid="SEL_wb_process"
                    label="Process"
                    options={processOptions}
                    value={form.process_id}
                    onChange={(v) =>
                      setForm({ ...form, process_id: v ? Number(v) : "" })
                    }
                    placeholder="Optional…"
                    clearable
                  />
                </div>
                <FormControl.SearchSelect
                  data-testid="SEL_wb_occurrence"
                  label="Occurrence"
                  options={OCCURRENCE_OPTIONS}
                  value={form.occurrence_type}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      occurrence_type: (v as string) || "Actual Event",
                    })
                  }
                />
                <FormControl.Textarea
                  data-testid="INP_wb_description"
                  label="Description"
                  required
                  rows={4}
                  placeholder="Please describe what happened, who was involved, and where..."
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
                <div className="report-incident-popup-upload-wrap">
                  <label className="report-incident-popup-upload-label">
                    Evidence
                  </label>
                  <label className="report-incident-popup-upload">
                    <input data-testid="INP_wb_evidence_file" type="file" multiple />
                    <Paperclip size={18} />
                    <span>Click to upload screenshots or documents</span>
                  </label>
                </div>
                <FormControl.Checkbox
                  data-testid="CHK_wb_anonymous"
                  label="Submit Anonymously"
                  checked={form.isAnonymous}
                  onChange={(checked) =>
                    setForm({ ...form, isAnonymous: checked })
                  }
                />
              </>
            )}

            {tab === "history" && (
              <>
                <div className="report-incident-popup-list-section-title">
                  My Active Incidents
                </div>
                {historyLoading && (
                  <div className="report-incident-popup-list-empty">
                    Loading…
                  </div>
                )}
                {!historyLoading && activeIncidents.length === 0 && (
                  <div className="report-incident-popup-list-empty">
                    No active incidents reported by you.
                  </div>
                )}
                {activeIncidents.map((inc) => (
                  <div key={inc.id} className="report-incident-popup-list-card">
                    <div className="report-incident-popup-list-card-top">
                      <span className="report-incident-popup-list-card-code">
                        {inc.incident_code}
                      </span>
                      <StatusBadge status={inc.remediation_status} />
                    </div>
                    <h4 className="report-incident-popup-list-card-title">
                      {inc.occurrence_type || "Incident"}
                    </h4>
                    <p className="report-incident-popup-list-card-desc">
                      {inc.incident_description}
                    </p>
                    {inc.reporter_feedback && (
                      <div className="report-incident-popup-list-card-feedback">
                        <div className="report-incident-popup-list-card-feedback-label">
                          <MessageSquare size={10} /> Admin Feedback
                        </div>
                        <p className="report-incident-popup-list-card-feedback-text">
                          {inc.reporter_feedback}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            {tab === "archived" && (
              <>
                <div className="report-incident-popup-list-section-title">
                  My Archived History
                </div>
                {historyLoading && (
                  <div className="report-incident-popup-list-empty">
                    Loading…
                  </div>
                )}
                {!historyLoading && archivedIncidents.length === 0 && (
                  <div className="report-incident-popup-list-empty">
                    No archived incidents.
                  </div>
                )}
                {archivedIncidents.map((inc) => (
                  <div
                    key={inc.id}
                    className="report-incident-popup-list-card archived"
                  >
                    <div className="report-incident-popup-list-card-top">
                      <span className="report-incident-popup-list-card-code">
                        {inc.incident_code}
                      </span>
                      <StatusBadge status={inc.remediation_status} />
                    </div>
                    <h4 className="report-incident-popup-list-card-title">
                      {inc.occurrence_type || "Incident"}
                    </h4>
                    <p className="report-incident-popup-list-card-desc">
                      {inc.incident_description}
                    </p>
                    {inc.reporter_feedback && (
                      <div className="report-incident-popup-list-card-feedback">
                        <div className="report-incident-popup-list-card-feedback-label">
                          <MessageSquare size={10} /> Admin Feedback
                        </div>
                        <p className="report-incident-popup-list-card-feedback-text">
                          {inc.reporter_feedback}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>

          {tab === "new" && (
            <div className="report-incident-popup-footer">
              <Button
                data-testid="BTN_wb_submit"
                variant="fill"
                icon={Send}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit Report"}
              </Button>
              {submitMsg && (
                <div
                  className={`report-incident-popup-msg ${submitMsg.kind}`}
                >
                  {submitMsg.text}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default WhistleBlowerBubble;
